import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { authHeaders, getAutomateApiUrl, getModelTier, setModelTier, MODEL_TIERS } from '../automate/apiBase';
import './Sidebar.css';
import ProjectsPanel from './ProjectsPanel';
import { getFeaturePath, parseFeatureProjectFromPath } from '../utils/routeUtils';
import { fetchProjects, setCurrentProject } from '../redux/projectsSlice';
import { resetSessionReduxState } from '../redux/sessionReset';
import { clearAuthSession } from '../auth/storage';
import { CodeLensIcon } from './icons/CodeLensIcon';
/* Icons: dashboard (layout panels), 9-dot grid, inbox (Automate), play (Test Automation), list (Auto Test Data), logout */
const IconDashboard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="7" height="9" x="3" y="3" rx="1.5" />
    <rect width="7" height="5" x="14" y="3" rx="1.5" />
    <rect width="7" height="9" x="14" y="12" rx="1.5" />
    <rect width="7" height="5" x="3" y="16" rx="1.5" />
  </svg>
);
const IconGrid = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="3" width="5" height="5" rx="0.5" />
    <rect x="16" y="3" width="5" height="5" rx="0.5" />
    <rect x="3" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="16" y="9.5" width="5" height="5" rx="0.5" />
    <rect x="3" y="16" width="5" height="5" rx="0.5" />
    <rect x="9.5" y="16" width="5" height="5" rx="0.5" />
    <rect x="16" y="16" width="5" height="5" rx="0.5" />
  </svg>
);
const IconInbox = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);
const IconPlay = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);
const IconFlask = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2h6" />
    <path d="M10 2v6.34a2 2 0 0 1-.4 1.2L4.7 16.8A2 2 0 0 0 6.3 20h11.4a2 2 0 0 0 1.6-3.2l-4.9-7.26a2 2 0 0 1-.4-1.2V2" />
    <path d="M6.5 14.5h11" />
  </svg>
);
const IconList = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>);
const IconCode = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);
const IconFileCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <polyline points="9 15 11 17 15 12.5" />
  </svg>
);
const IconBug = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="6" width="8" height="12" rx="4" />
    <path d="M12 6V4" />
    <path d="M9 4l-1.5-1.5" />
    <path d="M15 4l1.5-1.5" />
    <path d="M8 10H4" />
    <path d="M8 14H4" />
    <path d="M16 10h4" />
    <path d="M16 14h4" />
    <path d="M9.5 18l-2 2.5" />
    <path d="M14.5 18l2 2.5" />
  </svg>
);

const IconLogout = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const Sidebar = ({ setIsAuthenticated, userProfile }) => {
  // ----- Hooks + state -----
  const dispatch = useDispatch();
  const { allProjects = [], currentProject } = useSelector((state) => state.projects);
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const modelMenuRef = useRef(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [modelTier, setModelTierState] = useState(getModelTier);
  const headerProjectMenuRef = useRef(null);
  const [headerProjectMenuOpen, setHeaderProjectMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [projectsPanelOpen, setProjectsPanelOpen] = useState(false);
  const [primaryActive, setPrimaryActive] = useState('project');
  const [trialPeriodEndDate, setTrialPeriodEndDate] = useState(null);
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  const [trialTokenLimit, setTrialTokenLimit] = useState(3000000);
  const [userPlan, setUserPlan] = useState('trial');
  const [tokenLimit, setTokenLimit] = useState(3000000);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('warning');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  // ----- Derived values (pure functions of state/props/location) -----
  // Convert Redux projects to sidebar format
  const sidebarProjects = allProjects.map((p) => ({
    id: String(p.id),
    name: p.projectName,
    brief: p.brief,
  }));

    const primaryLinkItems = [
    { path: '/lens-library', label: 'Lens Library', icon: <CodeLensIcon width="22" height="22" /> },
    { path: '/requirement-intake', label: 'Automate', icon: <IconInbox /> },
    { path: '/test-automation', label: 'Test Automation', icon: <IconPlay /> },
    { path: '/brd-to-frd', label: 'Requirement Document', icon: <IconFileCheck /> },
    { path: '/automation-scripts', label: 'Automation Scripts', icon: <IconCode /> },
    { path: '/bug-report', label: 'Bug Report', icon: <IconBug /> },
  ];

  /** Which nav item the current route implies should be active (null if neither a feature nor home/project page). */
  const getPrimaryActiveForPath = (path) => {
    const { featureBase } = parseFeatureProjectFromPath(path);
    if (featureBase) return featureBase;
    if (path === '/home') return 'home';
    if (path.startsWith('/project/')) return 'project';
    return null;
  };

  /** Active project for AI features: URL (feature or project page) first, then Redux session selection */
  const getResolvedProjectIdForFeatures = () => {
    const { projectId: fromFeature } = parseFeatureProjectFromPath(location.pathname);
    if (fromFeature != null && String(fromFeature).trim() !== '') {
      return String(fromFeature);
    }
    const projectPageMatch = location.pathname.match(/^\/project\/([^/]+)/);
    if (projectPageMatch) {
      return projectPageMatch[1];
    }
    if (currentProject?.id != null && String(currentProject.id).trim() !== '') {
      return String(currentProject.id);
    }
    return null;
  };

  // On a feature page, the top header shows a compact breadcrumb + project switcher
  // instead of the generic greeting.
  const { featureBase: currentFeatureBase } = parseFeatureProjectFromPath(location.pathname);
  const isFeaturePage = !!currentFeatureBase;
  const currentFeatureItem = primaryLinkItems.find((item) => item.path === currentFeatureBase);
  const currentFeatureLabel = currentFeatureItem?.label || '';
  const headerProjectId = isFeaturePage ? getResolvedProjectIdForFeatures() : null;
  const headerProject = allProjects.find((p) => String(p.id) === String(headerProjectId));
  const headerProjectName = headerProject?.projectName || 'Select project';

  // ----- Effects -----
  // Refetch projects whenever the authenticated sidebar mounts (avoids showing another user's cached list)
  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (headerProjectMenuRef.current && !headerProjectMenuRef.current.contains(e.target)) {
        setHeaderProjectMenuOpen(false);
      }
      if (modelMenuRef.current && !modelMenuRef.current.contains(e.target)) {
        setModelMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkTrialPeriod = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${getAutomateApiUrl()}/api/auth/me`, {
            headers: authHeaders(),
          });
          if (response.data) {
            if (response.data.trialPeriodEndDate) setTrialPeriodEndDate(response.data.trialPeriodEndDate);
            if (response.data.totalTokensUsed !== undefined) setTotalTokensUsed(response.data.totalTokensUsed);
            if (response.data.plan) setUserPlan(response.data.plan);
            if (response.data.tokenLimit !== undefined) setTokenLimit(response.data.tokenLimit || 3000000);
            if (response.data.trialTokenLimit !== undefined) setTrialTokenLimit(response.data.trialTokenLimit);
          }
        }
      } catch (error) {
        console.error('Error checking trial period:', error);
      }
    };

    checkTrialPeriod();
  }, []);

  useEffect(() => {
    document.body.classList.toggle('sidebar-hidden', !isSidebarVisible);
    return () => document.body.classList.remove('sidebar-hidden');
  }, [isSidebarVisible]);

  // Update primaryActive based on current route (including /feature/projectId/:id),
  // and close the Projects panel on any navigation so it never lingers on a new page.
  useEffect(() => {
    const nextActive = getPrimaryActiveForPath(location.pathname);
    if (nextActive) {
      setPrimaryActive(nextActive);
    }
    setProjectsPanelOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ----- Handlers -----
  const handleLogout = async () => {
    try {
      await axios.post(`${getAutomateApiUrl()}/auth/logout`, {}, { headers: authHeaders() });
    } catch (_err) {
      // Client still clears local session even if the API call fails.
    }
    clearAuthSession();
    resetSessionReduxState();
    setIsAuthenticated(false);
    navigate('/login');
  };

  const getUserInitials = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return (userProfile.firstName.charAt(0) + userProfile.lastName.charAt(0)).toUpperCase();
    }
    return 'U';
  };

  const getFullDisplayName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      const lastInitial = userProfile.lastName.charAt(0).toUpperCase();
      return `${userProfile.firstName} ${lastInitial}`;
    }
    return userProfile?.firstName || userProfile?.username || 'User';
  };

  const getWelcomeName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    return userProfile?.firstName || userProfile?.username || 'User';
  };

  const getWelcomeDisplayText = () => getWelcomeName();

  const getTrialDaysLeft = () => {
    if (!trialPeriodEndDate || userPlan !== 'trial') return null;
    const end = new Date(trialPeriodEndDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const getRoleLabel = () => 'User Account';

  const handleProjectConfirm = () => {
    if (!selectedProjectId || !selectedFeature) return;
    const path = selectedFeature;
    const aiFeatures = ['/requirement-intake'];
    if (aiFeatures.includes(path)) {
      const currentLimit = userPlan === 'trial' ? trialTokenLimit : (userPlan === 'free' ? 1000000 : (userPlan === 'paid' ? tokenLimit : null));
      if (currentLimit !== null && totalTokensUsed >= currentLimit) {
        const planName = userPlan === 'trial' ? 'trial period' : (userPlan === 'free' ? 'free plan' : 'plan');
        setAlertMessage(`Your ${planName} token limit of ${currentLimit.toLocaleString()} tokens has been reached. Please contact support to continue using the service.`);
        setAlertVariant('warning');
        setTimeout(() => setAlertMessage(null), 3000);
        setShowProjectModal(false);
        setSelectedFeature(null);
        setSelectedProjectId(null);
        navigate('/profile');
        return;
      }
    }
    const fullProject = allProjects.find((p) => String(p.id) === String(selectedProjectId));
    if (fullProject) {
      dispatch(setCurrentProject(fullProject));
    }
    navigate(getFeaturePath(path, selectedProjectId));
    setShowProjectModal(false);
    setSelectedFeature(null);
    setSelectedProjectId(null);
    setNavOpen(false);
  };

  const handleSidebarToggle = () => {
    if (window.innerWidth <= 768) {
      setNavOpen(!navOpen);
      return;
    }
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleHeaderSwitchProject = (project) => {
    const fullProject = allProjects.find((p) => String(p.id) === String(project.id));
    if (fullProject) {
      dispatch(setCurrentProject(fullProject));
    }
    setHeaderProjectMenuOpen(false);
    if (currentFeatureBase) {
      navigate(getFeaturePath(currentFeatureBase, project.id));
    }
  };

  return (
    <>
      {alertMessage && (
        <Alert variant={alertVariant} dismissible onClose={() => setAlertMessage(null)} className="sidebar-alert" style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999, maxWidth: '400px' }}>
          {alertMessage}
        </Alert>
      )}
      <header className={`sidebar-app-header ${isSidebarVisible ? '' : 'sidebar-collapsed'}`}>
        <div className={`sidebar-header-logo-strip ${isSidebarVisible ? '' : 'sidebar-collapsed'}`}>
          <div className="sidebar-logo-block">
            <img src="/header-logo.png" alt="" className="sidebar-logo-icon" />
          </div>
          <button
            type="button"
            className="sidebar-header-hamburger"
            onClick={handleSidebarToggle}
            aria-expanded={window.innerWidth <= 768 ? navOpen : isSidebarVisible}
            aria-label={window.innerWidth <= 768 ? (navOpen ? 'Close menu' : 'Open menu') : (isSidebarVisible ? 'Hide sidebar' : 'Show sidebar')}
          >
            <svg className="sidebar-header-hamburger-icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <line x1="9.5" y1="4" x2="9.5" y2="20" />
            </svg>
          </button>
        </div>
        <div className="sidebar-header-main">
          {isFeaturePage ? (
            <div className="sidebar-header-breadcrumb">
              <span className="sidebar-header-breadcrumb-icon" aria-hidden="true"><IconGrid /></span>
              <span className="sidebar-header-breadcrumb-label">{currentFeatureLabel}</span>
              <div className="sidebar-header-project-switcher" ref={headerProjectMenuRef}>
                <button
                  type="button"
                  className="sidebar-header-project-trigger"
                  onClick={() => setHeaderProjectMenuOpen((open) => !open)}
                  aria-expanded={headerProjectMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="sidebar-header-project-name">{headerProjectName}</span>
                  <svg className={`sidebar-header-project-chevron ${headerProjectMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9" />
                  </svg>
                </button>
                {headerProjectMenuOpen && (
                  <div className="sidebar-header-project-dropdown" role="menu">
                    {sidebarProjects.length === 0 ? (
                      <div className="sidebar-header-project-dropdown-empty">No projects yet</div>
                    ) : (
                      sidebarProjects.map((project) => (
                        <button
                          key={project.id}
                          type="button"
                          role="menuitem"
                          className={`sidebar-header-project-dropdown-item ${String(project.id) === String(headerProjectId) ? 'active' : ''}`}
                          onClick={() => handleHeaderSwitchProject(project)}
                        >
                          {project.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="sidebar-welcome">{getWelcomeDisplayText()}!</p>
          )}
          <div className="sidebar-header-actions">
            <div className="sidebar-header-project-switcher" ref={modelMenuRef}>
              <button
                type="button"
                className="sidebar-header-project-trigger"
                onClick={() => setModelMenuOpen((open) => !open)}
                aria-expanded={modelMenuOpen}
                aria-haspopup="true"
                title={MODEL_TIERS.find((t) => t.value === modelTier)?.description || 'Model tier for AI-generated content across the app'}
              >
                <span className="sidebar-header-project-name">
                  Model: {MODEL_TIERS.find((t) => t.value === modelTier)?.label || 'Auto'}
                </span>
                <svg className={`sidebar-header-project-chevron ${modelMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>
              {modelMenuOpen && (
                <div className="sidebar-header-project-dropdown" role="menu">
                  {MODEL_TIERS.map((tier) => (
                    <button
                      key={tier.value}
                      type="button"
                      role="menuitem"
                      className={`sidebar-header-project-dropdown-item sidebar-header-model-item ${tier.value === modelTier ? 'active' : ''}`}
                      title={tier.description}
                      onClick={() => {
                        setModelTierState(setModelTier(tier.value));
                        setModelMenuOpen(false);
                      }}
                    >
                      <span className="sidebar-header-model-item-label">{tier.label}</span>
                      <span className="sidebar-header-model-item-desc">{tier.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {userPlan === 'trial' && getTrialDaysLeft() !== null && (
              <span className="sidebar-trial-text">
                Trial Period: {getTrialDaysLeft()} day{getTrialDaysLeft() !== 1 ? 's' : ''} left
              </span>
            )}
            <div className="sidebar-header-user-wrap" ref={userMenuRef}>
              <button type="button" className="sidebar-user-trigger" onClick={() => setUserMenuOpen(!userMenuOpen)} aria-expanded={userMenuOpen} aria-haspopup="true">
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{getFullDisplayName()}</span>
                  <span className="sidebar-user-role">{getRoleLabel()}</span>
                </div>
                <div className="sidebar-avatar">
                  {userProfile?.profilePhoto ? <img src={userProfile.profilePhoto} alt="" /> : <span>{getUserInitials()}</span>}
                </div>
                <svg className="sidebar-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6,9 12,15 18,9" /></svg>
              </button>
              {userMenuOpen && (
                <div className="sidebar-dropdown">
                  <NavLink to="/profile" className="sidebar-dropdown-item" onClick={() => setUserMenuOpen(false)}>Profile</NavLink>
                  <button type="button" className="sidebar-dropdown-item sidebar-dropdown-logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className={`sidebar sidebar-vertical ${navOpen ? 'nav-open' : ''} ${isSidebarVisible ? '' : 'sidebar-hidden'}`}>
        <div className="sidebar-body">
          <nav className="sidebar-primary-nav" aria-label="Primary">
            <span className="sidebar-primary-group-label">Workspace</span>
            <NavLink
              to="/home"
              end
              className={({ isActive }) => `sidebar-primary-link ${isActive ? 'active' : ''}`}
              onClick={() => setNavOpen(false)}
              aria-label="Dashboard"
            >
              <span className="sidebar-primary-icon"><IconDashboard /></span>
              <span className="sidebar-primary-label">Dashboard</span>
            </NavLink>
            <button
              type="button"
              className={`sidebar-primary-item ${primaryActive === 'project' ? 'active' : ''}`}
              onClick={() => {
                setProjectsPanelOpen((open) => {
                  const opening = !open;
                  if (opening) {
                    setPrimaryActive('project');
                  } else {
                    const restored = getPrimaryActiveForPath(location.pathname);
                    if (restored) setPrimaryActive(restored);
                  }
                  return opening;
                });
              }}
              aria-label="Projects"
              aria-expanded={projectsPanelOpen}
            >
              <span className="sidebar-primary-icon"><IconGrid /></span>
              <span className="sidebar-primary-label">Projects</span>
            </button>
            <span className="sidebar-primary-group-label">Features</span>
            {primaryLinkItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`sidebar-primary-link ${primaryActive === item.path ? 'active' : ''}`}
                aria-label={item.label}
                onClick={() => {
                  const path = item.path;
                  const aiFeatures = ['/requirement-intake'];
                  if (aiFeatures.includes(path)) {
                    const currentLimit = userPlan === 'trial' ? trialTokenLimit : (userPlan === 'free' ? 1000000 : (userPlan === 'paid' ? tokenLimit : null));
                    if (currentLimit !== null && totalTokensUsed >= currentLimit) {
                      const planName = userPlan === 'trial' ? 'trial period' : (userPlan === 'free' ? 'free plan' : 'plan');
                      setAlertMessage(`Your ${planName} token limit of ${currentLimit.toLocaleString()} tokens has been reached. Please contact support to continue using the service.`);
                      setAlertVariant('warning');
                      setTimeout(() => setAlertMessage(null), 3000);
                      navigate('/profile');
                      return;
                    }
                  }

                  const resolvedProjectId = getResolvedProjectIdForFeatures();
                  if (resolvedProjectId) {
                    navigate(getFeaturePath(path, resolvedProjectId));
                    setNavOpen(false);
                    return;
                  }

                  setSelectedProjectId(null);
                  setSelectedFeature(path);
                  setShowProjectModal(true);
                }}
              >
                <span className="sidebar-primary-icon">{item.icon}</span>
                <span className="sidebar-primary-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <footer className="sidebar-footer">
          <button type="button" className="sidebar-footer-logout" onClick={handleLogout} aria-label="Log out">
            <span className="sidebar-footer-logout-icon" aria-hidden="true"><IconLogout /></span>
            <span className="sidebar-footer-logout-label">Logout</span>
          </button>
        </footer>

        <button type="button" className="sidebar-mobile-toggle" onClick={() => setNavOpen(!navOpen)} aria-expanded={navOpen} aria-label={navOpen ? 'Close menu' : 'Open menu'}>
          <span className="sidebar-mobile-toggle-icon"><span /><span /><span /></span>
        </button>
      </div>
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <ProjectsPanel
        isOpen={projectsPanelOpen}
        onClose={() => {
          setProjectsPanelOpen(false);
          const restored = getPrimaryActiveForPath(location.pathname);
          if (restored) setPrimaryActive(restored);
        }}
        projects={sidebarProjects}
        onAddProject={() => {
          navigate('/home', { state: { openAddProject: true } });
          setProjectsPanelOpen(false);
        }}
      />

      {showProjectModal && (
        <div className="sidebar-project-picker-backdrop" onClick={() => { setShowProjectModal(false); setSelectedFeature(null); setSelectedProjectId(null); }} aria-hidden="true" />
      )}
      {showProjectModal && (
        <div className="sidebar-project-picker-modal" role="dialog" aria-modal="true" aria-labelledby="sidebar-project-picker-title">
          <h2 id="sidebar-project-picker-title" className="sidebar-project-picker-title">
            Select a project for {primaryLinkItems.find(item => item.path === selectedFeature)?.label || 'this feature'}
          </h2>
          <ul className="sidebar-project-picker-list">
            {sidebarProjects.map((proj) => (
              <li key={proj.id}>
                <button
                  type="button"
                  className={`sidebar-project-picker-item ${selectedProjectId === proj.id ? 'selected' : ''}`}
                  onClick={() => setSelectedProjectId(proj.id)}
                >
                  <span className="sidebar-project-picker-item-name">{proj.name}</span>
                  {proj.brief && <span className="sidebar-project-picker-item-brief">{proj.brief}</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="sidebar-project-picker-actions">
            <button
              type="button"
              className="sidebar-project-picker-add"
              onClick={() => {
                setShowProjectModal(false);
                setSelectedFeature(null);
                setSelectedProjectId(null);
                navigate('/home', { state: { openAddProject: true } });
              }}
            >
              Add Project
            </button>
            <button type="button" className="sidebar-project-picker-cancel" onClick={() => { setShowProjectModal(false); setSelectedFeature(null); setSelectedProjectId(null); }}>Cancel</button>
            <button type="button" className="sidebar-project-picker-ok" onClick={handleProjectConfirm} disabled={!selectedProjectId}>OK</button>
          </div>
        </div>
      )}

    </>
  );
};
export default Sidebar;
